import { Button, Form, Input, message, Spin } from "antd";
import { isAxiosError } from "axios";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@hooks";
import { ArrowLeft } from "@phosphor-icons/react";

export const Login = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [form] = Form.useForm();

  const { sendAccessLink, profile } = useAuth();
  const { data: profileData, isLoading: isLoadingData } = profile;

  const handleSendAccessLink = (data: { email: string }) => {
    setUserEmail(data.email);
    sendAccessLink.mutate(data.email, {
      onSuccess: () => {
        setEmailSent(true);
        message.success(`Access link sent to ${data.email}`);
      },
      onError: (error) => {
        const defaultMessage = "Error sending access link";
        if (isAxiosError(error)) {
          message.error(error.response?.data.message || defaultMessage);
        } else {
          message.error(defaultMessage);
        }
      },
    });
  };

  const handleResendEmail = () => {
    if (userEmail) {
      sendAccessLink.mutate(userEmail, {
        onSuccess: () => {
          message.success(`Access link resent to ${userEmail}`);
        },
        onError: (error) => {
          const defaultMessage = "Error resending access link";
          if (isAxiosError(error)) {
            message.error(error.response?.data.message || defaultMessage);
          } else {
            message.error(defaultMessage);
          }
        },
      });
    }
  };

  if (!isLoadingData && profileData?.email) {
    return <Navigate to="/" />;
  }

  return (
    <Spin spinning={sendAccessLink.isPending}>
      <div className="flex flex-col min-h-screen bg-charcoal bg-[url(/assets/images/ModlinkSchemPattern.png)] bg-cover bg-center bg-no-repeat">
        
        {/* Botão voltar ao website */}
        <div className="z-10 p-8 lg:p-12 flex justify-center">
          <a href="/" className="font-orator font-semibold text-lg md:text-xl text-orangicide tracking-wider w-fit flex items-center gap-3">
            <ArrowLeft size={22} weight="bold"/>
            Back to website
          </a>
        </div>

        {/* Layout horizontal: Logo à esquerda, Formulário à direita */}
        <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-end flex-1 w-full z-10 px-8 gap-12 lg:gap-24">
          
          {/* Logo grande à esquerda */}
          <div className="flex-shrink-0 lg:absolute lg:left-50">
            <img 
              src='/assets/images/ModlinkLogo.png' 
              alt="Mod-Link" 
              className="w-28 md:w-44 lg:w-60" 
            />
          </div>

          {/* Formulário à direita */}
          <div className="w-full max-w-md lg:mr-12">
            {!emailSent ? (
              <div className="flex flex-col">
                {/* Título LOG IN */}
                <h1 className="font-displayed text-white text-4xl lg:text-5xl tracking-wider mb-4">
                  LOG IN
                </h1>

                <Form
                  form={form}
                  name="login"
                  onFinish={handleSendAccessLink}
                  layout="vertical"
                  className="login-page-form"
                >
                  {/* Campo de Email com clipPath */}
                  <Form.Item
                    name="email"
                    rules={[{ required: true, message: "please enter your email!" }]}
                    className="login-email-form-item"
                  >
                    <div className="relative">
                      <span className="absolute -top-8 right-0 font-orator text-orangicide text-base tracking-wider z-10">
                        Email
                      </span>
                      <div 
                        className="relative border border-white w-full"
                        style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
                      >
                        {/* Corner decoration - bottom left */}
                        <div
                          className="pointer-events-none absolute bottom-0 top-0 left-0 bg-white"
                          style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                        />
                        <div
                          className="pointer-events-none absolute bottom-0 right-0 bg-white"
                          style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                        />
                        <Input 
                          placeholder="email@email.com"
                          className="login-email-input font-orator !bg-transparent !border-none !text-white/70 w-full sm:!w-112 !h-12 !text-base"
                        />
                      </div>
                    </div>
                  </Form.Item>

                  {/* Botão SEND ACCESS LINK */}
                  <div className="flex justify-center mt-12">
                    <Form.Item>
                      <Button 
                        htmlType="submit" 
                        className="font-displayed !font-normal !text-2xl sm:!text-3xl tracking-wider w-full sm:w-112 !h-13 btn-orangicide"
                      >
                        SEND ACCESS LINK
                      </Button>
                    </Form.Item>
                  </div>
                </Form>

                {/* Texto explicativo */}
                <p className="font-orator font-semibold text-white text-center text-sm lg:text-xl tracking-wider mt-6">
                  We'll send you an access link<br />
                  to your email for secure,<br />
                  password-free login.
                </p>
              </div>
            ) : (
              <div className="relative flex flex-col items-center mt-12">
                {/* Ícone de email posicionado na borda superior */}
                <div className="icon-badge-orangicide absolute -top-10 left-1/2 -translate-x-1/2 z-10">
                  <img src="/assets/icons/OpenEnvelope.svg" alt="Email" className="w-10 h-10 pb-1" />
                </div>
                
                <div 
                  className="relative border-4 border-white p-8 pt-10 text-center"
                  style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
                >
                  {/* Corner decorations */}
                  <div
                    className="pointer-events-none absolute top-0 left-0 bg-white"
                    style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0 bg-white"
                    style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />

                  <h2 className="font-displayed text-4xl text-white mb-6">ACCESS LINK SENT</h2>
                <p className="font-orator !font-normal text-xl text-orangicide tracking-wider mb-6">
                  Check your email inbox for the access link.
                  <br /><br />
                  [May be in spam or junk]
                </p>
                  <Button 
                    type="link" 
                    className="btn-orangicide font-displayed !font-normal tracking-wider !text-3xl !w-full !h-12 mt-4" 
                    onClick={handleResendEmail}
                    loading={sendAccessLink.isPending}
                  >
                    RESEND
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Spin>
  );
};